import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useForm, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
  YES_NO_CHOICES,
  GDS_QUESTIONS,
  initialFormData,
} from "./geriatricDepressionScale.config";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import DynamicSelectInput from "../../../../../FormFields/CustomSelect/DynamicSelectInput";
import { createGeriatricDepressionScale } from "../../../../../../api/services/assessment";
import { getCarePlans } from "../../../../../../api/services/core";
import { QK } from "../../../../../../base/qk";

const CreateGeriatricDepressionScaleModal = ({ open, handleClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();

  const { data: carePlans } = useQuery([QK.carePlans], () => getCarePlans());

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: initialFormData,
  });

  const createAssessmentMutation = useMutation(
    (assessmentData) => createGeriatricDepressionScale(assessmentData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QK.geriatricDepressionScales, clientID]);
        enqueueSnackbar("GDS assessment created successfully", {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to create assessment",
          { variant: "error" }
        );
      },
    }
  );

  const onSubmit = async (data) => {
    try {
      const payload = { ...data, client: clientID };
      await createAssessmentMutation.mutateAsync(payload);
      handleCloseModal();
    } catch (error) {
      // Error is handled by the mutation's onError callback
    }
  };

  const handleCloseModal = () => {
    reset();
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      scroll="body"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Create Geriatric Depression Scale (GDS) Assessment
          </Typography>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ p: 2 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="care_plan"
                  control={control}
                  rules={{ required: "Care plan is required" }}
                  render={({ field }) => (
                    <DynamicSelectInput
                      options={carePlans}
                      label="Which care plan is this linked to? *"
                      name="care_plan"
                      onChange={field.onChange}
                      value={field.value}
                      error={!!errors.care_plan}
                      helperText={errors.care_plan?.message}
                      required
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Assessment Questions
            </Typography>

            <Grid container spacing={2}>
              {GDS_QUESTIONS.map((q) => (
                <Grid item xs={12} md={6} key={q.name}>
                  <Controller
                    name={q.name}
                    control={control}
                    rules={{
                      validate: (value) =>
                        (value !== null && value !== undefined) ||
                        "This field is required",
                    }}
                    render={({ field }) => (
                      <CustomSelect
                        name={q.name}
                        label={`${q.label} *`}
                        selectedValue={field.value}
                        onChange={field.onChange}
                        options={YES_NO_CHOICES}
                        error={!!errors[q.name]}
                        helperText={errors[q.name]?.message}
                        required
                      />
                    )}
                  />
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="comments"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="comments"
                      label="Comments"
                      value={field.value}
                      onChange={field.onChange}
                      multiline
                      rows={4}
                      placeholder="Enter any additional comments here."
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCloseModal} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
          disabled={createAssessmentMutation.isLoading}
        >
          {createAssessmentMutation.isLoading
            ? "Creating..."
            : "Create Assessment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateGeriatricDepressionScaleModal;
